import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./KeepPostedPaywall.module.scss"

/**
 * KeepPostedPaywall (`/keepposted`) — an original, self-contained EXPIRATION /
 * RENEWAL paywall for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md · concept doc: claude-paywalls/keep-posted.md
 * The four prior EXPIRATION concepts are Deep Freeze (cold ice/fear, passive
 * countdown), Unbroken (warm calendar heatmap, plan-toggle FLOODS the year with
 * light), Ahead (forking road you scrub a traveller up) and Keystone (masonry
 * arch you drag a capstone into). This is none of them — and it deliberately
 * avoids the exhausted "grey→colour bloom / light-flood" mechanic every warm
 * memory paywall now leans on.
 *   - Layout:      a retro SPLIT-FLAP "Solari" STATUS BOARD (departure-board
 *                  grid) — a big flip readout of your protection status over a
 *                  track of month tiles, mounted in a slate console with a
 *                  physical LEVER. Not a hero+card, calendar, road or arch.
 *   - Interaction: PULL THE LEVER → the whole board FLIP-CASCADES forward; the
 *                  plan you pick sets how far it rolls (Yearly rolls a full year,
 *                  Monthly one month). Mechanical flaps, not a colour bloom.
 *   - Register:    retro-mechanical, orderly-confident, satisfying — the analog
 *                  clack of a station board. Not cold fear, earned-pride streak,
 *                  hopeful road or load-bearing gravity.
 *   - Art:         LIGHT warm cream flap faces + a slate console; brand blue is
 *                  the KEPT/active flap, amber is the PAUSED/today state.
 *                  Non-dark, non-skeuomorphic-illustration.
 *   - Value frame: continuity of automatic protection framed for RENEWAL — "keep
 *                  it posted." Contrast + honest loss-aversion.
 *
 * Honest & App Store compliant: renewal keeps the ongoing automatic backup, sync
 * and full-resolution originals running for the months ahead. It never implies
 * data loss — the board says plainly "Your photos are always yours — nothing is
 * ever deleted." Coverage dates are the real today + term, computed live. No fake
 * countdown, no fake scarcity; the savings figure is exact yearly-vs-monthly
 * arithmetic. Yearly is the featured, default plan and the board's default roll;
 * monthly appears smaller and secondary.
 */

type PlanKey = "yearly" | "monthly"

const MONTHS = [
  "JAN",
  "FEB",
  "MAR",
  "APR",
  "MAY",
  "JUN",
  "JUL",
  "AUG",
  "SEP",
  "OCT",
  "NOV",
  "DEC",
]

// deterministic warm "photographic" swatches for the month tiles — self-contained,
// no external asset egress (the headless browser can't reach Unsplash)
const SWATCHES = [
  "linear-gradient(135deg,#ffd9a8,#f5a15f)",
  "linear-gradient(135deg,#bcd4ff,#6f95e8)",
  "linear-gradient(135deg,#ffc7c2,#e88a86)",
  "linear-gradient(135deg,#cfe9c9,#7db98f)",
  "linear-gradient(135deg,#e7d4ff,#a688d8)",
  "linear-gradient(135deg,#ffe1b0,#e0a24e)",
  "linear-gradient(135deg,#c7e6ff,#5f8fd6)",
  "linear-gradient(135deg,#ffd2e0,#e07fa4)",
  "linear-gradient(135deg,#d8e6b8,#9bb066)",
  "linear-gradient(135deg,#c9d8ff,#7b7fe0)",
  "linear-gradient(135deg,#ffcaa0,#ec7f66)",
  "linear-gradient(135deg,#bfe9df,#66b3a2)",
]

// month coverage → the "KEPT THRU <MON YEAR>" label. offset 0 = today (paused).
function coverLabel(offsetMonths: number): string {
  const now = new Date()
  const d = new Date(now.getFullYear(), now.getMonth() + offsetMonths, 1)
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

// ── One split-flap character. Real Solari-style fold: the top leaf drops to
// reveal the incoming char while the bottom leaf swings up into place. ────────
const FlapChar = ({
  char,
  delay = 0,
  tone,
}: {
  char: string
  delay?: number
  tone: "kept" | "paused"
}) => {
  const [display, setDisplay] = useState(char)
  const [prev, setPrev] = useState(char)
  const [flipping, setFlipping] = useState(false)
  const timers = useRef<number[]>([])

  useEffect(() => {
    if (char === display) return
    const t1 = window.setTimeout(() => {
      setPrev(display)
      setFlipping(true)
      const t2 = window.setTimeout(() => {
        setDisplay(char)
        setFlipping(false)
      }, 260)
      timers.current.push(t2)
    }, delay)
    timers.current.push(t1)
    return () => {
      timers.current.forEach((t) => window.clearTimeout(t))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [char])

  const cls = `${styles.flap} ${tone === "paused" ? styles.flapPaused : ""} ${
    char === " " ? styles.flapBlank : ""
  }`

  return (
    <span className={cls} aria-hidden="true">
      {/* settled halves — the top reveals the incoming char behind the falling
          leaf; the bottom holds the outgoing char until the new leaf lands */}
      <span className={`${styles.leaf} ${styles.leafTopStatic}`}>
        <span className={styles.glyph}>{flipping ? char : display}</span>
      </span>
      <span className={`${styles.leaf} ${styles.leafBottomStatic}`}>
        <span className={styles.glyph}>{display}</span>
      </span>
      {flipping && (
        <>
          <span className={`${styles.leaf} ${styles.leafTopFlip}`}>
            <span className={styles.glyph}>{prev}</span>
          </span>
          <span className={`${styles.leaf} ${styles.leafBottomFlip}`}>
            <span className={styles.glyph}>{char}</span>
          </span>
        </>
      )}
      <span className={styles.seam} />
    </span>
  )
}

const FlapWord = ({
  text,
  tone,
  baseDelay = 0,
}: {
  text: string
  tone: "kept" | "paused"
  baseDelay?: number
}) => {
  // pad so the readout width is stable between "· TODAY ·" and "AUG 2027"
  const padded = text.padEnd(8, " ").slice(0, 8)
  return (
    <span className={styles.flapWord}>
      {padded.split("").map((c, i) => (
        <FlapChar key={i} char={c} tone={tone} delay={baseDelay + i * 55} />
      ))}
    </span>
  )
}

const Check = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    <path
      d="M5 13l4 4L19 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const KeepPostedPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")
  // months of coverage the board currently shows: 0 = paused/today
  const [cover, setCover] = useState(0)
  const [pulling, setPulling] = useState(false)
  const rollRef = useRef<number | undefined>(undefined)

  const targetFor = (p: PlanKey) => (p === "yearly" ? 12 : 1)

  // animate the tile track lighting up (cascade) toward `to`
  const rollTo = useCallback(
    (to: number) => {
      if (rollRef.current != null) window.clearInterval(rollRef.current)
      if (reduceMotion) {
        setCover(to)
        return
      }
      setCover((from) => {
        const dir = to > from ? 1 : -1
        let cur = from
        rollRef.current = window.setInterval(() => {
          cur += dir
          setCover(cur)
          if (cur === to && rollRef.current != null) {
            window.clearInterval(rollRef.current)
            rollRef.current = undefined
          }
        }, 90)
        return from
      })
    },
    [reduceMotion],
  )

  const pullLever = useCallback(
    (p: PlanKey) => {
      setPulling(true)
      window.setTimeout(() => setPulling(false), 420)
      rollTo(targetFor(p))
    },
    [rollTo],
  )

  const choosePlan = useCallback(
    (p: PlanKey) => {
      setPlan(p)
      native.logMixpanelEvent("html_pricing_toggle", { plan: p })
      pullLever(p)
    },
    [pullLever],
  )

  // Auto-demo once: sit PAUSED, then roll the board forward to the featured
  // yearly term so the resolved hero reads full coverage without interaction.
  useEffect(() => {
    if (reduceMotion) {
      setCover(12)
      return
    }
    const id = window.setTimeout(() => rollTo(12), 620)
    return () => {
      window.clearTimeout(id)
      if (rollRef.current != null) window.clearInterval(rollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const paused = cover === 0
  const readout = paused ? "· TODAY ·" : coverLabel(cover)
  const tone: "kept" | "paused" = paused ? "paused" : "kept"

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={styles.root}>
      <div className={styles.paper} aria-hidden="true" />

      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandName}>Private Photo Vault</span>
          <span className={styles.brandTag}>PREMIUM</span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <h1 className={styles.title}>Keep your vault posted.</h1>
        <p className={styles.sub}>
          Your Premium protection is paused. Renew and the board rolls forward —
          every month ahead stays backed up, private and full quality.
        </p>
      </div>

      {/* ── THE BOARD ────────────────────────────────────────────────────── */}
      <div className={styles.console}>
        <div className={styles.consoleBar}>
          <span className={styles.consoleLabel}>Vault protection</span>
          <span
            className={`${styles.statusDot} ${paused ? styles.dotPaused : styles.dotKept}`}
          >
            {paused ? "PAUSED" : "ACTIVE"}
          </span>
        </div>

        <div className={styles.readoutRow}>
          <span className={styles.readoutPrefix}>
            {paused ? "COVERED THRU" : "KEPT THRU"}
          </span>
          <FlapWord text={readout} tone={tone} />
        </div>

        {/* the year-ahead track of month flap tiles */}
        <div
          className={styles.track}
          role="img"
          aria-label={
            paused
              ? "Protection paused as of today"
              : `Protected through ${coverLabel(cover)}`
          }
        >
          {Array.from({ length: 12 }).map((_, i) => {
            const lit = i < cover
            return (
              <span
                key={i}
                className={`${styles.tile} ${lit ? styles.tileLit : ""}`}
                style={{
                  transitionDelay: lit ? `${Math.min(i, 11) * 20}ms` : "0ms",
                }}
              >
                <span
                  className={styles.tileSwatch}
                  style={{ background: SWATCHES[i] }}
                />
                <span className={styles.tileCheck}>
                  <Check size={11} />
                </span>
              </span>
            )
          })}
        </div>

        <div className={styles.trackCaption}>
          <span>{coverLabel(1).slice(0, 3)}</span>
          <span className={styles.trackArrow} aria-hidden="true">
            {paused ? "protection paused" : "kept, month by month"}
          </span>
          <span>{paused ? "—" : coverLabel(cover).slice(0, 3)}</span>
        </div>

        {/* physical lever on the console */}
        <button
          className={`${styles.lever} ${pulling ? styles.leverPull : ""}`}
          onClick={() => pullLever(plan)}
          aria-label="Pull to renew and roll the board forward"
        >
          <span className={styles.leverSlot} aria-hidden="true" />
          <span className={styles.leverArm}>
            <span className={styles.leverKnob} />
          </span>
          <span className={styles.leverText}>PULL</span>
        </button>
      </div>

      <p className={styles.hint}>
        {paused
          ? "Pull the lever — or pick a plan below to roll it forward."
          : `Rolled forward to ${coverLabel(cover)}. Pick a plan to lock it in.`}
      </p>

      {/* ── PLANS: yearly featured ───────────────────────────────────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => choosePlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyLeft}>
            <span className={styles.yearlyName}>
              Renew a full year
              {savingsPct > 0 && (
                <span className={styles.saveTag}>SAVE {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlySub}>
              Rolls your protection 12 months forward
            </span>
          </span>
          <span className={styles.yearlyRight}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>per year</span>
            <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => choosePlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Or renew month to month</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      {/* real Premium features, listed as board status lines */}
      <ul className={styles.manifest} aria-label="What renewing keeps running">
        {[
          "Automatic full-resolution backup",
          "Private &amp; encrypted — only you",
          "In sync on every device",
        ].map((label, i) => (
          <li key={i} className={styles.manifestRow}>
            <span className={styles.manifestCheck}>
              <Check size={11} />
            </span>
            <span
              className={styles.manifestLabel}
              dangerouslySetInnerHTML={{ __html: label }}
            />
            <span className={styles.manifestTag}>KEPT</span>
          </li>
        ))}
      </ul>

      <div className={styles.dock}>
        <p className={styles.reassure}>
          Your photos are always yours — nothing is ever deleted. Renewing keeps
          the automatic backup, syncing &amp; full-resolution originals running.
        </p>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Renew — free for 7 days"
                : "Renew for a year"
              : "Renew monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
        <div className={styles.footerLinks}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

export default KeepPostedPaywall
